import { API_RESPONSE } from '../pages/Team/Reports/apis/api-reports'
import Axios from 'axios'
import Config from '../config'
import { DurationUnitType } from 'dayjs/plugin/duration'
import storageHelper from '../helpers/storage'
const { API_SIGNUP } = Config

export const ApiSignUp = Axios.create({
    baseURL: API_SIGNUP,
})

export const getIdTokenFromAuth0 = async ({ accessToken, email }: { accessToken: string; email: string }) =>
    ApiSignUp.post<{ data: { idToken: string } }>(
        '/v1/auth/auth0',
        { email },
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    ).then(({ data }) => data.data.idToken)

export const autoLogin = (profileId: string) =>
    Axios.post<PostAutoLogin>(
        `${API_SIGNUP}/v1/auth/auto-login`,
        { profileId },
        {
            headers: {
                Authorization: `Bearer ${storageHelper.getToken() ?? ''}`,
            },
        }
    ).then(({ data }) => data.linkCode)

export const getOrganizationReferral = (courseId: string) =>
    ApiSignUp.get<GetOrganizationReferralResponse>(`/admin/ecosystem/organizations/referral/${courseId}`).then(
        ({ data }) => data.data
    )

export const getUserCourses = () =>
    ApiSignUp.get<GetUserCoursesResponse>('/v1/users/courses').then(
        ({
            data: {
                data: { courses, ...rest },
            },
        }) => ({ ...rest, courses: courses.filter(({ role }) => role !== 'student') })
    )

export const getAdminUserCourses = () =>
    ApiSignUp.get<GetAdminUserCoursesResponse>('/v1/users/admin/courses').then(({ data }) => data.data)

export const fetchProfileStatus = (profileId: string) =>
    ApiSignUp.get<Api<GetProfileStatus>>(`/admin/profiles?profileId=${profileId}`).then(({ data }) => data.data)

// Responses
type GetOrganizationReferralResponse = API_RESPONSE<GetOrganizationReferral>
type GetUserCoursesResponse = API_RESPONSE<{ courses: UserCoursesDTO[] }>
type GetAdminUserCoursesResponse = API_RESPONSE<AdminUserCoursesData>

// Interfaces
type Api<T> = {
    data: T
    success: boolean
    message: string
    code: number
}

type GetProfileStatus = {
    courseId: string
    algorithmId: string | null
    displayName: string
    name: string | null
    lastname: string | null
    urlImage: string | null
    teamNumber: number | null
    currentRoom: 'general' | 'team' | null
}

interface PostAutoLogin {
    linkCode: string
    expires: string
}

interface GetOrganizationReferral {
    referral: string
    actNetworkId: string
}

export type UserCoursesDTO = {
    role: string
    isExpertMentor: boolean
    timesMentor: number
    timesFacilitator: number
    votes: number
    end: null | string
    start: null | string
    status: CourseStatus
    courseId: string
    terms: boolean
    profileId: string
    networkId: null | string
    networkName: null | string
    organizationName: null | string
    courseName: string
    slug: null | string
    mongoCourseName: string
    enrolledAt: string
    'access-token': string
    requirePayment: boolean
    asyncCourse: boolean
    offlineCourse: boolean
    algorithmId: string | null
    alreadyAssigned: boolean
    courseFlags: {
        helpRequestType: 'tutor' | 'mentor'
        enableSharedContent: boolean
        enabledSharedCanvas: boolean
        sdkVersion: number
    }
    nextClass: {
        open: string
        start: string
        delay: string
        absent: string
        end: string
        duration: [number, DurationUnitType]
    } | null
    meetings: {
        general: MeetingDTO | null
        team: MeetingDTO | null
        useFirstStepAsGeneral?: boolean
    } | null
    startDate?: string | null
    endDate?: string | null
    teamId?: string | null
}

export interface NetworkDTO {
    networkId: string | null
    networkName: string | null
    slug: string | null
    courses: UserCoursesDTO[]
}

export interface AdminUserCoursesData {
    networks: NetworkDTO[]
}

type CourseStatus =
    | 'unknown' // Without progressiveSchedule
    | 'enrolled' // With progressiveSchedule.start > today
    | 'studying' // With progressiveSchedule.start <= today & without progressiveSchedule.end
    | 'finished' // With progressiveSchedule.end > today
    | 'deserted' // With active = false

interface MeetingDTO {
    provider:
        | 'egg'
        | 'zoom-sdk'
        | 'zoom-sdk-auto'
        | 'zoom'
        | 'zoom-by-table'
        | 'zoom-hybrid'
        | 'meet'
        | 'teams'
        | 'whatsapp'
        | 'jitsi'
    url: string
}
